import type { ComputedBeneficiaryPayload } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryFetched';
import type { ComputedBeneficiaryListItemPayload } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiariesFetched';
import type PlainDate from '@Domain/PlainDate';
import isPurgeApproaching from '@UI/Store/Beneficiaries/Views/isPurgeApproaching';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * Carries the detail shape (INS included). Deliberately NOT derived from `BeneficiaryListItemView`:
 * `Object.freeze(this)` in a base constructor would prevent a subclass from assigning its own fields,
 * so the two Views duplicate their eleven common fields rather than sharing a base class.
 */
class BeneficiaryView {
	readonly id: number;
	readonly firstName: string;
	readonly lastName: string;
	readonly birthName: string | null;
	readonly birthDate: PlainDate | null;
	readonly homeAddress: string | null;
	readonly phoneNumber: string | null;
	readonly status: LabeledValueView;
	readonly archivedAt: Date | null;
	readonly purgeScheduledAt: Date | null;
	readonly facilityId: number;
	readonly nationalHealthIdentifier: string | null;
	readonly assignedUserIds: readonly number[];
	readonly interviewsCount: number;
	readonly documentsCount: number;
	readonly nursingStaffMemberId: number | null;
	readonly homeCareStaffMemberId: number | null;

	constructor(payload: ComputedBeneficiaryPayload) {
		this.id = payload.id;
		this.firstName = payload.firstName;
		this.lastName = payload.lastName;
		this.birthName = payload.birthName;
		this.birthDate = payload.birthDate;
		this.homeAddress = payload.homeAddress;
		this.phoneNumber = payload.phoneNumber;
		this.status = new LabeledValueView(payload.status);
		this.archivedAt = payload.archivedAt;
		this.purgeScheduledAt = payload.purgeScheduledAt;
		this.facilityId = payload.facility.id;
		this.nationalHealthIdentifier = payload.nationalHealthIdentifier;
		this.assignedUserIds = payload.assignedUsers.map((assignedUser) => assignedUser.id);
		this.interviewsCount = payload.interviewsCount;
		this.documentsCount = payload.documentsCount;
		// `?? null`: a payload built before this field existed omits the key entirely (`undefined`),
		// treated exactly like an explicit `null` rather than as a distinct third state.
		this.nursingStaffMemberId = (payload.nursingStaffMember ?? null)?.id ?? null;
		this.homeCareStaffMemberId = (payload.homeCareStaffMember ?? null)?.id ?? null;

		Object.freeze(this);
	}

	/**
	 * Surname first, French administrative order — the identity of a care record, not a contact card.
	 * The surname's uppercasing is a *display* concern (`BeneficiaryName`, `text-transform`): applying
	 * it here would put it in the DOM, where a screen reader spells it letter by letter and a copy
	 * carries it into a letter. Casing is never normalized on input either — `de La Fontaine`,
	 * `McDonald` and `d'Arcy` are correct as typed, and a mangled name on a health record is an
	 * identity error.
	 */
	get fullName(): string {
		return `${this.lastName} ${this.firstName}`;
	}

	/**
	 * The birth name, only when it differs from the name in use. It is the reference identity trait
	 * of a care record: collected on the form, it has to be readable somewhere.
	 */
	get birthNameMention(): string | null {
		if (this.birthName === null || this.birthName === this.lastName) {
			return null;
		}

		return `né(e) ${this.birthName}`;
	}

	get isArchived(): boolean {
		return this.status.value === 'archived';
	}

	get isPurgeApproaching(): boolean {
		return isPurgeApproaching(this.purgeScheduledAt);
	}

	// `PUT …/status` answers the list shape (no INS, no staff members): rebuild the detail from the
	// list-item payload while keeping the INS and the two staff member ids this instance already
	// holds, rather than losing them from the screen.
	withListFields(payload: ComputedBeneficiaryListItemPayload): BeneficiaryView {
		return new BeneficiaryView({
			...payload,
			nationalHealthIdentifier: this.nationalHealthIdentifier,
			nursingStaffMember: this.nursingStaffMemberId !== null ? { id: this.nursingStaffMemberId } : null,
			homeCareStaffMember:
				this.homeCareStaffMemberId !== null ? { id: this.homeCareStaffMemberId } : null,
		});
	}

	// Applies a save of the personnel block: the ids travel on the action, so the fields are set
	// directly from them, no lookup needed.
	withStaffMembers(
		nursingStaffMemberId: number | null,
		homeCareStaffMemberId: number | null,
	): BeneficiaryView {
		return new BeneficiaryView({
			id: this.id,
			firstName: this.firstName,
			lastName: this.lastName,
			birthName: this.birthName,
			birthDate: this.birthDate,
			homeAddress: this.homeAddress,
			phoneNumber: this.phoneNumber,
			status: this.status,
			archivedAt: this.archivedAt,
			purgeScheduledAt: this.purgeScheduledAt,
			facility: { id: this.facilityId },
			assignedUsers: this.assignedUserIds.map((assignedUserId) => ({ id: assignedUserId })),
			interviewsCount: this.interviewsCount,
			nationalHealthIdentifier: this.nationalHealthIdentifier,
			nursingStaffMember: nursingStaffMemberId !== null ? { id: nursingStaffMemberId } : null,
			homeCareStaffMember: homeCareStaffMemberId !== null ? { id: homeCareStaffMemberId } : null,
			documentsCount: this.documentsCount,
		});
	}
}

export default BeneficiaryView;
